import React, { useRef, useState } from 'react';
import { useLanguage, Language } from '../contexts/LanguageContext';

// Página a la que Stripe devuelve al cliente después de pagar la foto de $12
// (?s=<checkout session id>). El cliente sube una foto (cámara o galería), la
// función photo-process la valida con Snap2Pass y le envía por correo la foto
// 2x2 y la hoja 4x6. Todo automático.
const ENDPOINT = 'https://jcbxwtuhshcyzlhpikxr.supabase.co/functions/v1/photo-process';
const ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpjYnh3dHVoc2hjeXpsaHBpa3hyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTA0NDc5MzAsImV4cCI6MjEwNjAyMzkzMH0.TCCTiwKNauedwdX3R0_Ag0MCZId2nZGmuLIN86FcH-U';
const CONTACT_EMAIL = 'info@noorpassportservices.com';
type Strings = {
  title: string;
  intro: string;
  tipsTitle: string;
  tips: string[];
  choose: string;
  sending: string;
  waitNote: string;
  okTitle: string;
  okBody: string;
  retryTitle: string;
  retryBody: string;
  left: string;
  exhaustedTitle: string;
  exhaustedBody: string;
  doneTitle: string;
  doneBody: string;
  missingTitle: string;
  missingBody: string;
  notFound: string;
  tooBig: string;
  generic: string;
  disclaimer: string;
};
const T: Record<Language, Strings> = {
  es: {
    title: 'Suba su foto de pasaporte',
    intro: 'Tome una selfie con su teléfono o elija una foto reciente de su galería. La validamos automáticamente y le enviamos por correo su foto 2x2 y la hoja 4x6 lista para imprimir.',
    tipsTitle: 'Para mejores resultados',
    tips: ['Mire de frente a la cámara, con expresión neutral y la boca cerrada.', 'Fondo claro y liso, sin sombras detrás de usted.', 'Luz pareja en el rostro. Sin lentes, gorra ni sombrero.', 'Cámara a la altura de los ojos; muestre cabeza y hombros.'],
    choose: 'Tomar o elegir foto',
    sending: 'Procesando su foto…',
    waitNote: 'Esto toma unos 20 segundos. No cierre esta página.',
    okTitle: '¡Listo! Su foto fue validada',
    okBody: 'Le enviamos por correo su foto 2x2 y la hoja 4x6. Revise su bandeja de entrada (y la carpeta de correo no deseado).',
    retryTitle: 'Esta foto no pasó la validación',
    retryBody: 'Intente de nuevo siguiendo los consejos de arriba: fondo claro y liso, luz pareja, mirada al frente y sin lentes.',
    left: 'Intentos restantes:',
    exhaustedTitle: 'Usó todos sus intentos',
    exhaustedBody: 'Ya recibimos el aviso y nos comunicaremos con usted por correo para ayudarle.',
    doneTitle: 'Su foto ya fue enviada',
    doneBody: 'Revise su correo: ahí están su foto 2x2 y la hoja 4x6.',
    missingTitle: 'Falta el enlace de su pago',
    missingBody: 'Abra esta página desde el enlace que le enviamos por correo después de pagar. Si necesita ayuda, escríbanos a',
    notFound: 'No encontramos su pago. Si ya pagó, escríbanos a',
    tooBig: 'La foto es demasiado grande. Intente con otra.',
    generic: 'Ocurrió un problema. Intente de nuevo o escríbanos a',
    disclaimer: 'Validamos su foto con una herramienta automática y no alteramos su imagen. La aceptación final corresponde al Departamento de Estado.'
  },
  en: {
    title: 'Upload your passport photo',
    intro: 'Take a selfie with your phone or choose a recent photo from your gallery. We validate it automatically and email you your 2x2 photo and a print-ready 4x6 sheet.',
    tipsTitle: 'For best results',
    tips: ['Face the camera directly with a neutral expression and mouth closed.', 'Plain, light background with no shadows behind you.', 'Even lighting on your face. No glasses, cap or hat.', 'Camera at eye level; show your head and shoulders.'],
    choose: 'Take or choose photo',
    sending: 'Processing your photo…',
    waitNote: 'This takes about 20 seconds. Please keep this page open.',
    okTitle: 'Done! Your photo was validated',
    okBody: 'We emailed you your 2x2 photo and the 4x6 sheet. Check your inbox (and your spam folder).',
    retryTitle: 'This photo did not pass validation',
    retryBody: 'Please try again following the tips above: plain light background, even lighting, looking straight ahead, no glasses.',
    left: 'Attempts left:',
    exhaustedTitle: 'You have used all your attempts',
    exhaustedBody: 'We have been notified and will contact you by email to help.',
    doneTitle: 'Your photo was already sent',
    doneBody: 'Check your email: your 2x2 photo and 4x6 sheet are there.',
    missingTitle: 'Your payment link is missing',
    missingBody: 'Open this page from the link we emailed you after payment. If you need help, write to',
    notFound: 'We could not find your payment. If you already paid, write to',
    tooBig: 'The photo is too large. Please try another one.',
    generic: 'Something went wrong. Try again or write to',
    disclaimer: 'We validate your photo with an automated tool and do not alter your image. Final acceptance is up to the Department of State.'
  },
  ht: {
    title: 'Voye foto paspò ou',
    intro: 'Pran yon selfi ak telefòn ou oswa chwazi yon foto resan nan galri ou. Nou valide l otomatikman epi nou voye foto 2x2 ou a ak fèy 4x6 a pa imèl.',
    tipsTitle: 'Pou pi bon rezilta',
    tips: ['Gade kamera a direkteman, san emosyon, bouch fèmen.', 'Fon klè ak plat, pa gen lonbraj dèyè ou.', 'Limyè egal sou figi ou. Pa gen linèt, kasket oswa chapo.', 'Kamera a nivo je ou; montre tèt ak zepòl ou.'],
    choose: 'Pran oswa chwazi foto',
    sending: 'Nap trete foto ou…',
    waitNote: 'Sa pran apeprè 20 segonn. Pa fèmen paj sa a.',
    okTitle: 'Fini! Foto ou valide',
    okBody: 'Nou voye foto 2x2 ou a ak fèy 4x6 a pa imèl. Gade nan bwat resepsyon ou (ak nan spam).',
    retryTitle: 'Foto sa a pa pase validasyon',
    retryBody: 'Eseye ankò swiv konsèy yo anlè a: fon klè ak plat, limyè egal, gade direk, san linèt.',
    left: 'Esè ki rete:',
    exhaustedTitle: 'Ou itilize tout esè ou yo',
    exhaustedBody: 'Nou resevwa avi a epi nou pral kontakte ou pa imèl pou ede ou.',
    doneTitle: 'Nou deja voye foto ou',
    doneBody: 'Gade imèl ou: foto 2x2 ou a ak fèy 4x6 a la.',
    missingTitle: 'Lyen peman ou manke',
    missingBody: 'Louvri paj sa a ak lyen nou voye ba ou pa imèl apre peman an. Si w bezwen èd, ekri nou nan',
    notFound: 'Nou pa jwenn peman ou. Si ou deja peye, ekri nou nan',
    tooBig: 'Foto a two gwo. Eseye yon lòt.',
    generic: 'Gen yon pwoblèm. Eseye ankò oswa ekri nou nan',
    disclaimer: 'Nou valide foto ou ak yon zouti otomatik epi nou pa chanje imaj ou. Se Depatman Deta ki deside akseptasyon final la.'
  },
  vi: {
    title: 'Tải ảnh hộ chiếu của quý vị',
    intro: 'Chụp ảnh tự sướng bằng điện thoại hoặc chọn một ảnh gần đây trong thư viện. Chúng tôi tự động kiểm tra và gửi email ảnh 2x2 cùng tờ 4x6 sẵn sàng để in.',
    tipsTitle: 'Để có kết quả tốt nhất',
    tips: ['Nhìn thẳng vào máy ảnh, vẻ mặt trung tính, ngậm miệng.', 'Nền sáng, trơn, không có bóng phía sau.', 'Ánh sáng đều trên khuôn mặt. Không đeo kính, mũ.', 'Máy ảnh ngang tầm mắt; thấy đầu và vai.'],
    choose: 'Chụp hoặc chọn ảnh',
    sending: 'Đang xử lý ảnh của quý vị…',
    waitNote: 'Việc này mất khoảng 20 giây. Vui lòng giữ trang này mở.',
    okTitle: 'Xong! Ảnh của quý vị đã được xác nhận',
    okBody: 'Chúng tôi đã gửi email ảnh 2x2 và tờ 4x6. Hãy kiểm tra hộp thư đến (và thư rác).',
    retryTitle: 'Ảnh này chưa đạt yêu cầu',
    retryBody: 'Vui lòng thử lại theo các lưu ý ở trên: nền sáng trơn, ánh sáng đều, nhìn thẳng, không đeo kính.',
    left: 'Số lần còn lại:',
    exhaustedTitle: 'Quý vị đã dùng hết số lần thử',
    exhaustedBody: 'Chúng tôi đã nhận được thông báo và sẽ liên hệ qua email để hỗ trợ.',
    doneTitle: 'Ảnh của quý vị đã được gửi',
    doneBody: 'Hãy kiểm tra email: ảnh 2x2 và tờ 4x6 ở đó.',
    missingTitle: 'Thiếu liên kết thanh toán',
    missingBody: 'Hãy mở trang này từ liên kết chúng tôi đã gửi qua email sau khi thanh toán. Nếu cần hỗ trợ, hãy viết cho chúng tôi tại',
    notFound: 'Chúng tôi không tìm thấy thanh toán của quý vị. Nếu đã thanh toán, hãy viết cho chúng tôi tại',
    tooBig: 'Ảnh quá lớn. Vui lòng thử ảnh khác.',
    generic: 'Đã xảy ra sự cố. Hãy thử lại hoặc viết cho chúng tôi tại',
    disclaimer: 'Chúng tôi kiểm tra ảnh bằng công cụ tự động và không chỉnh sửa hình ảnh. Việc chấp nhận cuối cùng thuộc về Bộ Ngoại giao.'
  }
};
type Status = 'idle' | 'sending' | 'ok' | 'retry' | 'exhausted' | 'done' | 'notfound' | 'error' | 'toobig';

// Reduce the picture to at most 1600px on its longest side as a JPEG.
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const max = 1600;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('canvas'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', 0.9).split(',')[1]);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('img'));
    };
    img.src = url;
  });
}
export function FotoPage() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  const [session] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('s') || '';
    } catch {
      return '';
    }
  });
  const [status, setStatus] = useState<Status>('idle');
  const [left, setLeft] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mail = <a className="underline font-medium text-brand-textPrimary" href={`mailto:${CONTACT_EMAIL}`} data-id="element-450" data-mp="1ai6i13toala4">{CONTACT_EMAIL}</a>;
  const btn = 'block w-full text-center px-4 py-4 rounded-xl bg-brand-textPrimary text-white font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 disabled:opacity-50';
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setStatus('sending');
    try {
      const photo = await toBase64(file);
      if (photo.length > 5900000) {
        setStatus('toobig');
        return;
      }
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: ANON,
          Authorization: 'Bearer ' + ANON
        },
        body: JSON.stringify({
          session,
          photo,
          lang: language
        })
      });
      let data: any = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (typeof data.attemptsLeft === 'number') setLeft(data.attemptsLeft);
      if (data.ok) setStatus('ok');else if (data.error === 'already_delivered') setStatus('done');else if (data.error === 'exhausted') setStatus('exhausted');else if (data.error === 'not_found' || res.status === 404) setStatus('notfound');else if (data.error === 'retry') setStatus('retry');else setStatus('error');
    } catch {
      setStatus('error');
    }
  }
  if (!session) {
    return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-id="element-451" data-mp="1vg4avkioyprs">
        <h1 className="text-3xl font-bold text-brand-textPrimary mb-4" data-id="element-452" data-mp="20gi96auhqlcm">{t.missingTitle}</h1>
        <p className="text-brand-textSecondary" data-id="element-453" data-mp="13s2g44s69tur">{t.missingBody} {mail}</p>
      </div>;
  }
  const canUpload = status === 'idle' || status === 'retry' || status === 'error' || status === 'toobig';
  return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-id="element-454" data-mp="1cpw9p7yjy9e8">
      <h1 className="text-3xl sm:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-455" data-mp="1hift4ljx7yqy">{t.title}</h1>
      <p className="text-lg text-brand-textSecondary mb-6" data-id="element-456" data-mp="k3jyc89hxiq3">{t.intro}</p>

      {(status === 'idle' || status === 'retry' || status === 'error' || status === 'toobig' || status === 'sending') && <div className="rounded-xl border border-brand-border bg-white p-4 mb-6" data-id="element-457" data-mp="1sbzcph6nb5lx">
          <h2 className="font-semibold text-brand-textPrimary mb-2" data-id="element-458" data-mp="1ndxqindxaq68">{t.tipsTitle}</h2>
          <ul className="list-disc pl-5 text-brand-textSecondary space-y-1" data-id="element-459" data-mp="2152w063z2coh">
            {t.tips.map(x => <li key={x} data-id="element-460" data-mp="1x3hci1wxn05b">{x}</li>)}
          </ul>
        </div>}

      {status === 'sending' && <div role="status" className="rounded-xl border border-brand-border bg-white p-4 mb-6" data-id="element-461" data-mp="19dwp96tqxgq8">
          <p className="font-semibold text-brand-textPrimary" data-id="element-462" data-mp="14etovzopnyol">{t.sending}</p>
          <p className="text-sm text-brand-textSecondary mt-1" data-id="element-463" data-mp="14gwo9abr745z">{t.waitNote}</p>
        </div>}

      {status === 'ok' && <div role="status" className="rounded-xl border border-green-300 bg-green-50 p-4 mb-6" data-id="element-464" data-mp="1dfrrqeney05l">
          <p className="font-semibold text-brand-textPrimary" data-id="element-465" data-mp="2tthzpwhnl8ki">{t.okTitle}</p>
          <p className="text-brand-textSecondary mt-1" data-id="element-466" data-mp="2tvkxj2p1s5zs">{t.okBody}</p>
        </div>}
      {status === 'done' && <div role="status" className="rounded-xl border border-green-300 bg-green-50 p-4 mb-6" data-id="element-467" data-mp="2xbxigolc1ecz">
          <p className="font-semibold text-brand-textPrimary" data-id="element-468" data-mp="31d109gmxxkgu">{t.doneTitle}</p>
          <p className="text-brand-textSecondary mt-1" data-id="element-469" data-mp="31ay043s2qaye">{t.doneBody}</p>
        </div>}
      {status === 'retry' && <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-6" data-id="element-470" data-mp="2scciqbyc91z7">
          <p className="font-semibold text-brand-textPrimary" data-id="element-471" data-mp="3g2fbkua2ubyv">{t.retryTitle}</p>
          <p className="text-brand-textSecondary mt-1" data-id="element-472" data-mp="3g4jcr4glcwvg">{t.retryBody}</p>
          {left !== null && <p className="text-sm text-brand-textSecondary mt-2" data-id="element-473" data-mp="3l3cnwq8eobsg">{t.left} {left}</p>}
        </div>}
      {status === 'exhausted' && <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-6" data-id="element-474" data-mp="3baxbe8fvofey">
          <p className="font-semibold text-brand-textPrimary" data-id="element-475" data-mp="2v54vgf69po6">{t.exhaustedTitle}</p>
          <p className="text-brand-textSecondary mt-1" data-id="element-476" data-mp="7m3oaovgj3vn">{t.exhaustedBody}</p>
        </div>}
      {status === 'notfound' && <div role="alert" className="rounded-xl border border-red-300 bg-red-50 p-4 mb-6" data-id="element-477" data-mp="2f6zu219blq7t">
          <p className="text-brand-textPrimary" data-id="element-478" data-mp="2b43har6goobs">{t.notFound} {mail}</p>
        </div>}
      {status === 'toobig' && <div role="alert" className="rounded-xl border border-red-300 bg-red-50 p-4 mb-6" data-id="element-479" data-mp="265s90kvtelvo">
          <p className="text-brand-textPrimary" data-id="element-480" data-mp="217gy06tsa5bv">{t.tooBig}</p>
        </div>}
      {status === 'error' && <div role="alert" className="rounded-xl border border-red-300 bg-red-50 p-4 mb-6" data-id="element-481" data-mp="2yt4k9ywiz79o">
          <p className="text-brand-textPrimary" data-id="element-482" data-mp="2tvct573pfp6j">{t.generic} {mail}</p>
        </div>}

      {canUpload && <>
          {/* Sin atributo capture: el teléfono ofrece elegir entre cámara y galería. */}
          <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="sr-only" id="foto-input" data-id="element-483" data-mp="2k2xas8ohwyl4" />
          <label htmlFor="foto-input" className={btn + ' cursor-pointer'} data-id="element-484" data-mp="3ctp3uxa9l748">{t.choose}</label>
        </>}

      <p className="text-sm text-brand-textSecondary mt-6" data-id="element-485" data-mp="3k53gfysdweww">{t.disclaimer}</p>
    </div>;
}